import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { MenuItem } from 'primeng/api/menuitem';
import { LayoutComponent } from './layout.component';
import { MenuItemWithClaims } from './layout.model';
import { MenuService } from './services/menu.service';

@Component({
  selector: 'app-menu',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <ul app-submenu [items]="model" [root]="true" class="layout-menu layout-main-menu clearfix"
        [reset]="reset" [visible]="true" [parentActive]="true"></ul>
    `
})
export class AppMenuComponent implements OnInit {
  @Input() reset = false;

  public model!: MenuItemWithClaims[];

  constructor(private menuService: MenuService) { }

  ngOnInit(): void {
    this.menuService.getMenuItems().subscribe(items => {
      this.model = items.filter(i => i.visible === true || typeof i.claims !== 'undefined');
    });
  }
}

@Component({
  /* eslint-disable @angular-eslint/component-selector */
  selector: '[app-submenu]',
  /* eslint-enable @angular-eslint/component-selector */
  template: `
        <ng-template ngFor let-child let-i="index" [ngForOf]="items">
          <ng-container *hasClaim="child.claims;value:child.claimsValues;operator:child.claimsOperator">
            <li [ngClass]="{'active-menuitem': isActive(i)}" [class]="child.badgeStyleClass || ''" *ngIf="child.visible === false ? false : true">
                <a [href]="child.url||'#'" (click)="itemClick($event,child,i)" (mouseenter)="onMouseEnter(i)"
                   class="ripplelink" *ngIf="!child.routerLink"
                    [attr.tabindex]="!visible ? '-1' : null" [attr.target]="child.target" style="padding: 10px 5px;">
                    <i [ngClass]="child.icon || ''"></i><span>{{child.label}}</span>
                    <i class="fa fa-fw fa-angle-down menuitem-toggle-icon" *ngIf="child.items"></i>
                    <span class="menuitem-badge" *ngIf="child.badge">{{child.badge}}</span>
                </a>

                <a (click)="itemClick($event,child,i)" (mouseenter)="onMouseEnter(i)" class="ripplelink" *ngIf="child.routerLink"
                    [routerLink]="child.routerLink" routerLinkActive="active-menuitem-routerlink"
                   [routerLinkActiveOptions]="{exact: true}" [attr.tabindex]="!visible ? '-1' : null" [attr.target]="child.target" style="padding: 10px 5px;">
                    <i [ngClass]="child.icon || ''"></i><span>{{child.label}}</span>
                    <i class="fa fa-fw fa-angle-down menuitem-toggle-icon" *ngIf="child.items"></i>
                    <span class="menuitem-badge" *ngIf="child.badge">{{child.badge}}</span>
                </a>
                <div class="layout-menu-tooltip">
                    <div class="layout-menu-tooltip-arrow"></div>
                    <div class="layout-menu-tooltip-text">{{child.label}}</div>
                </div>
                <div class="submenu-arrow" *ngIf="child.items"></div>
                <ul app-submenu [items]="child.items" *ngIf="child.items" [visible]="isActive(i)" [reset]="reset" [parentActive]="isActive(i)"
                    [@children]="(
                      (app.isSlim() || app.isHorizontal()) && root ?
                      (isActive(i) ? 'visible' : 'hidden') :
                      (isActive(i) ? 'visibleAnimated' : 'hiddenAnimated')
                    )"></ul>
            </li>
          </ng-container>
        </ng-template>
    `,
  animations: [
    trigger('children', [
      state('hiddenAnimated', style({
        height: '0px'
      })),
      state('visibleAnimated', style({
        height: '*'
      })),
      state('visible', style({
        display: 'block'
      })),
      state('hidden', style({
        display: 'none'
      })),
      transition('visibleAnimated => hiddenAnimated', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)')),
      transition('hiddenAnimated => visibleAnimated', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)'))
    ])
  ]
})
export class AppSubMenuComponent {
  @Input() items!: MenuItemWithClaims[];
  @Input() root = false;
  @Input() visible = true;

  @Input() get reset(): boolean {
    return this._reset;
  }
  set reset(val: boolean) {
    this._reset = val;

    if (this._reset && (this.app.isHorizontal() || this.app.isSlim())) {
      this.activeIndex = null;
    }
  }

  @Input() get parentActive(): boolean {
    return this._parentActive;
  }
  set parentActive(val: boolean) {
    this._parentActive = val;

    if (!this._parentActive) {
      this.activeIndex = null;
    }
  }

  private _reset = false;
  private _parentActive = false;
  private activeIndex: number | null | undefined;

  constructor(public app: LayoutComponent) { }

  itemClick(event: Event, item: MenuItem, index: number): void {
    if (this.root) {
      this.app.menuHoverActive = !this.app.menuHoverActive;
    }

    // avoid processing disabled items
    if (item.disabled) {
      event.preventDefault();
      return;
    }

    // activate current item and deactivate active sibling if any
    this.activeIndex = (this.activeIndex === index) ? null : index;

    // execute command
    if (item.command) {
      item.command({ originalEvent: event, item: item });
    }

    // prevent hash change
    if (item.items || (!item.url && !item.routerLink)) {
      setTimeout(() => {
        if (this.app.layoutMenuScrollerViewChild) {
          this.app.layoutMenuScrollerViewChild.moveBar();
        }
      }, 450);
      event.preventDefault();
    }

    // hide menu
    if (!item.items) {
      if (this.app.isHorizontal() || this.app.isSlim()) {
        this.app.resetMenu = true;
      } else {
        this.app.resetMenu = false;
      }

      this.app.overlayMenuActive = false;
      this.app.staticMenuMobileActive = false;
      this.app.menuHoverActive = !this.app.menuHoverActive;
    }
  }

  onMouseEnter(index: number): void {
    if (this.root && this.app.menuHoverActive && (this.app.isHorizontal() || this.app.isSlim())
      && !this.app.isMobile() && !this.app.isTablet()) {
      this.activeIndex = index;
    }
  }

  isActive(index: number): boolean {
    return this.activeIndex === index;
  }
}
